import * as THREE from 'three';
import { MaterialLibrary } from '../assets/MaterialLibrary';
import { EnemyFactory } from '../assets/modelFactories/EnemyFactory';
import { SoldierFactory } from '../assets/modelFactories/SoldierFactory';
import { TowerFactory, type TowerType } from '../assets/modelFactories/TowerFactory';
import { WorldFactory } from '../assets/modelFactories/WorldFactory';
import { InputController } from '../core/InputController';
import { Loop } from '../core/Loop';
import { createRenderer, resizeRenderer } from '../core/Renderer';
import { ArtilleryShell } from '../entities/ArtilleryShell';
import { Enemy } from '../entities/Enemy';
import { Objective } from '../entities/Objective';
import { Projectile } from '../entities/Projectile';
import { Soldier } from '../entities/Soldier';
import { Tower, TOWER_STATS } from '../entities/Tower';
import { AudioSystem } from '../systems/AudioSystem';
import { CameraRig } from '../systems/CameraRig';
import { CombatSystem } from '../systems/CombatSystem';
import { DebugTools, type DebugTuning } from '../systems/DebugTools';
import { EconomySystem } from '../systems/EconomySystem';
import { Hud } from '../systems/Hud';
import { VfxSystem } from '../systems/VfxSystem';
import { WaveSystem, type Wave } from '../systems/WaveSystem';

export class Game {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(48, 1, 0.1, 80);
  private readonly loop: Loop;
  private readonly input: InputController;
  private readonly hud = new Hud();
  private readonly audio = new AudioSystem();
  private readonly materials = new MaterialLibrary();
  private readonly towerFactory = new TowerFactory(this.materials);
  private readonly enemyFactory = new EnemyFactory(this.materials);
  private readonly soldierFactory = new SoldierFactory(this.materials);
  private readonly worldFactory = new WorldFactory(this.materials);
  private readonly combat = new CombatSystem();
  private readonly economy = new EconomySystem(200);
  private readonly vfx: VfxSystem;

  private readonly world: ReturnType<WorldFactory['create']>;
  private readonly waveSystem: WaveSystem;
  private readonly objective: Objective;
  private readonly towers: Tower[] = [];
  private readonly soldiers: Soldier[] = [];
  private readonly enemies: Enemy[] = [];
  private readonly projectiles: Projectile[] = [];
  private readonly shells: ArtilleryShell[] = [];
  private readonly paths: THREE.CatmullRomCurve3[];
  private readonly shellRadius = 2.0;
  private readonly shellDamage = 35;

  private readonly tuning: DebugTuning = {
    exposure: 1.05,
    maxDpr: 2,
  };
  private readonly debugTools: DebugTools;

  private gameState: 'menu' | 'playing' | 'wave-complete' | 'gameover' | 'victory' = 'menu';
  private lives = 20;
  private frame = 0;
  private elapsed = 0;
  private speedMultiplier = 1;
  private placementGhost: THREE.Mesh | null = null;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.renderer = createRenderer(canvas);
    this.renderer.toneMappingExposure = this.tuning.exposure;

    this.world = this.worldFactory.create();
    this.paths = this.world.paths.map(
      (points) => new THREE.CatmullRomCurve3(points, false, 'catmullrom', 0.35),
    );
    this.objective = new Objective(this.world.objectivePosition, this.lives);
    this.waveSystem = new WaveSystem(
      this.enemyFactory,
      this.paths,
      this.world.spawnPositions,
      this.world.objectivePosition,
      this.world.validGround,
    );
    this.vfx = new VfxSystem(this.scene, this.materials);

    this.input = new InputController(
      canvas,
      this.camera,
      (type) => this.onSelectTower(type),
      (active) => this.onSpeedToggle(active),
    );

    this.debugTools = new DebugTools(this.tuning, () => {
      this.renderer.toneMappingExposure = this.tuning.exposure;
      resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr);
    });

    this.hud.onTowerSelect((type) => this.onHudSelectTower(type));
    this.hud.onOverlayAction(() => this.onOverlayAction());
    this.hud.onSpeedToggle((active) => this.onSpeedToggle(active));

    this.waveSystem.onWaveStart((wave) => this.onWaveStart(wave));
    this.waveSystem.onEnemySpawn((enemy) => {
      this.enemies.push(enemy);
      this.scene.add(enemy.group);
    });
    this.waveSystem.onShellDrop(() => this.spawnShell());

    this.createScene();
    this.hud.setTotalWaves(this.waveSystem.totalWaves);
    this.hud.showOverlay('诺曼底防线', '盟军即将登陆。建造防御塔，守住海滩。', '开始战斗');

    resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr);
    this.loop = new Loop(
      (delta, elapsed) => this.update(delta, elapsed),
      () => this.render(),
    );
    this.publishDiagnostics();
  }

  start(): void {
    this.loop.start();
  }

  dispose(): void {
    this.loop.stop();
    this.input.dispose();
    this.audio.dispose();
    this.debugTools.dispose();
    this.vfx.clear();
    for (const tower of this.towers) tower.dispose();
    for (const soldier of this.soldiers) soldier.dispose();
    for (const enemy of this.enemies) enemy.dispose();
    for (const projectile of this.projectiles) projectile.dispose();
    this.materials.dispose();
    this.renderer.dispose();
    window.__THREE_GAME_DIAGNOSTICS__ = undefined;
  }

  private update(delta: number, _elapsed: number): void {
    this.frame += 1;
    const dt = Math.min(delta, 0.05) * this.speedMultiplier;
    this.elapsed += dt;

    resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr);
    this.input.update();

    this.hud.updateBanner(dt);

    if (this.gameState === 'playing') {
      this.waveSystem.update(dt);

      for (const shell of this.shells) {
        const hit = shell.update(dt);
        if (hit) {
          this.vfx.spawnExplosion(hit.position, 1.6, true);
          this.audio.explosion(1);
          for (const tower of this.towers) {
            if (!tower.active) continue;
            const dx = tower.group.position.x - hit.position.x;
            const dz = tower.group.position.z - hit.position.z;
            if (dx * dx + dz * dz <= hit.radius * hit.radius) {
              tower.takeDamage(hit.damage);
            }
          }
          for (const soldier of this.soldiers) {
            if (!soldier.active) continue;
            const dx = soldier.group.position.x - hit.position.x;
            const dz = soldier.group.position.z - hit.position.z;
            if (dx * dx + dz * dz <= hit.radius * hit.radius) {
              soldier.takeDamage(hit.damage);
            }
          }
        }
      }

      for (const enemy of this.enemies) {
        const targetTower = this.findEnemyTargetTower(enemy);
        const attackEvent = enemy.update(dt, this.elapsed, targetTower);
        if (attackEvent) {
          const muzzle = attackEvent.position.clone().add(new THREE.Vector3(0, 0.6, 0));
          const targetPos = attackEvent.target.group.position.clone().add(new THREE.Vector3(0, 0.7, 0));
          this.vfx.spawnEnemyMuzzleFlash(muzzle);
          this.vfx.spawnTracer(muzzle, targetPos);
          this.audio.shoot(enemy.stats.type === 'tank' || enemy.stats.type === 'landingcraft' ? 'cannon' : 'mg');
        }
      }

      const events = this.combat.update(
        dt,
        this.towers,
        this.soldiers,
        this.enemies,
        this.projectiles,
        this.objective.position,
        this.materials,
        (projectile) => {
          this.projectiles.push(projectile);
          this.scene.add(projectile.group);
        },
      );

      for (const event of events) {
        switch (event.type) {
          case 'shot':
            this.audio.shoot(event.towerType);
            this.vfx.spawnMuzzleFlash(event.position);
            break;
          case 'soldier-shot':
            this.audio.shoot('mg');
            this.vfx.spawnMuzzleFlash(event.position);
            break;
          case 'hit':
            this.vfx.spawnExplosion(event.position, event.splash ? 1.4 : 0.6, event.splash);
            if (event.splash) this.audio.explosion(0.8);
            break;
          case 'enemy-death':
            this.economy.earn(event.enemy.stats.reward);
            this.vfx.spawnExplosion(event.position, event.enemy.stats.type === 'tank' ? 1.8 : 1, false);
            this.audio.explosion(event.enemy.stats.type === 'tank' ? 1.2 : 0.7);
            break;
          case 'objective-damage':
            this.lives = Math.max(0, this.lives - event.amount);
            this.objective.takeDamage(event.amount);
            if (this.lives <= 0) {
              this.setGameOver();
            }
            break;
        }
      }

      this.vfx.update(dt);
      this.cleanupDeadEntities();
      this.checkWaveComplete();
    }

    this.updatePlacementGhost();
    this.handlePlacement();
    this.updateHud();
    this.publishDiagnostics();
  }

  private render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  private createScene(): void {
    this.scene.background = new THREE.Color('#1a1c1a');
    this.scene.fog = new THREE.Fog('#1a1c1a', 22, 46);

    const hemisphere = new THREE.HemisphereLight('#f6f1df', '#2b322d', 1.5);
    this.scene.add(hemisphere);

    const sun = new THREE.DirectionalLight('#fff1bf', 2.4);
    sun.position.set(-6, 11, 8);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.near = 0.5;
    sun.shadow.camera.far = 35;
    sun.shadow.camera.left = -18;
    sun.shadow.camera.right = 18;
    sun.shadow.camera.top = 16;
    sun.shadow.camera.bottom = -16;
    this.scene.add(sun);

    this.scene.add(this.world.root);

    this.spawnInitialSoldiers();

    new CameraRig(this.camera);
  }

  private onSelectTower(type: TowerType | null): void {
    this.hud.setSelectedTower(type);
    this.canvas.classList.toggle('placing', type !== null);
    if (!type && this.placementGhost) {
      this.scene.remove(this.placementGhost);
      this.placementGhost.geometry.dispose();
      this.placementGhost = null;
    }
  }

  private onHudSelectTower(type: TowerType | null): void {
    if (!type) return;
    if (this.input.selectedTower === type) {
      this.input.selectTower(null);
    } else {
      if (this.economy.canAfford(TOWER_STATS[type].cost)) {
        this.input.selectTower(type);
        this.audio.uiClick();
      } else {
        this.audio.error();
      }
    }
  }

  private onSpeedToggle(active: boolean): void {
    this.speedMultiplier = active ? 2.5 : 1;
    this.hud.setSpeedActive(active);
    this.input.setSpeedActive(active);
  }

  private onOverlayAction(): void {
    if (this.gameState === 'menu') {
      this.gameState = 'playing';
      this.hud.hideOverlay();
      this.waveSystem.startNextWave();
      this.audio.startMusic();
    } else if (this.gameState === 'wave-complete') {
      this.gameState = 'playing';
      this.hud.hideOverlay();
      this.waveSystem.startNextWave();
    } else if (this.gameState === 'gameover' || this.gameState === 'victory') {
      this.restart();
    }
  }

  private onWaveStart(wave: Wave): void {
    this.hud.showWaveBanner(wave.title, wave.subtitle);
  }

  private checkWaveComplete(): void {
    if (this.waveSystem.isWaiting && !this.waveSystem.isComplete) {
      const activeEnemies = this.enemies.some((e) => e.active || (!e.active && !e.reachedObjective && e.health > 0));
      if (!activeEnemies) {
        this.onWaveComplete();
      }
    }

    if (this.waveSystem.isComplete) {
      const activeEnemies = this.enemies.some((e) => e.active);
      if (!activeEnemies) {
        this.setVictory();
      }
    }
  }

  private onWaveComplete(): void {
    this.gameState = 'wave-complete';
    const nextWave = this.waveSystem.currentWaveIndex + 2;
    const waveBonus = 60 + this.waveSystem.currentWaveIndex * 25;
    this.economy.earn(waveBonus);
    if (nextWave <= this.waveSystem.totalWaves) {
      this.hud.showOverlay(
        '波次完成',
        `获得资金奖励 $${waveBonus}。\n准备迎接第 ${nextWave} 波进攻。`,
        '开始下一波',
      );
    }
  }

  private setGameOver(): void {
    this.gameState = 'gameover';
    this.audio.stopMusic();
    this.hud.showOverlay('防线失守', '盟军突破了你的防御。', '重新部署');
  }

  private setVictory(): void {
    this.gameState = 'victory';
    this.audio.stopMusic();
    this.hud.showOverlay('胜利', '你成功守住了诺曼底防线。', '再次战斗');
  }

  private restart(): void {
    this.audio.stopMusic();
    this.gameState = 'menu';
    this.lives = 20;
    this.objective.reset();
    this.economy.reset();
    this.speedMultiplier = 1;
    this.hud.setSpeedActive(false);
    this.input.setSpeedActive(false);
    this.waveSystem.reset();

    for (const tower of this.towers) {
      this.scene.remove(tower.group);
      tower.dispose();
    }
    this.towers.length = 0;

    for (const soldier of this.soldiers) {
      this.scene.remove(soldier.group);
      soldier.dispose();
    }
    this.soldiers.length = 0;

    for (const enemy of this.enemies) {
      this.scene.remove(enemy.group);
      enemy.dispose();
    }
    this.enemies.length = 0;

    for (const projectile of this.projectiles) {
      this.scene.remove(projectile.group);
      projectile.dispose();
    }
    this.projectiles.length = 0;

    for (const shell of this.shells) {
      this.scene.remove(shell.group);
      shell.dispose();
    }
    this.shells.length = 0;

    this.vfx.clear();
    this.onSelectTower(null);
    this.hud.showOverlay('诺曼底防线', '盟军即将登陆。建造防御塔，守住海滩。', '开始战斗');
  }

  private updatePlacementGhost(): void {
    const type = this.input.selectedTower;
    if (!type) return;

    if (!this.placementGhost) {
      const range = TOWER_STATS[type].range;
      const geometry = new THREE.RingGeometry(range - 0.05, range, 64);
      geometry.rotateX(-Math.PI / 2);
      this.placementGhost = new THREE.Mesh(geometry, this.materials.marker);
      this.placementGhost.position.y = 0.03;
      this.scene.add(this.placementGhost);
    }

    this.placementGhost.position.x = this.input.hoverPosition.x;
    this.placementGhost.position.z = this.input.hoverPosition.z;

    const valid = this.isValidPlacement(this.input.hoverPosition, type);
    this.placementGhost.material = valid ? this.materials.marker : this.materials.markerInvalid;
  }

  private handlePlacement(): void {
    const placement = this.input.consumePlacement();
    if (!placement) return;

    const stats = TOWER_STATS[placement.type];
    if (!this.economy.canAfford(stats.cost)) {
      this.audio.error();
      return;
    }

    if (!this.isValidPlacement(placement.position, placement.type)) {
      this.audio.error();
      return;
    }

    if (this.economy.spend(stats.cost)) {
      this.buildTower(placement.type, placement.position);
      this.audio.uiClick();
    }
  }

  private isValidPlacement(position: THREE.Vector3, _type: TowerType): boolean {
    if (!this.world.validGround.containsPoint(new THREE.Vector2(position.x, position.z))) {
      return false;
    }

    const pathThreshold = 1.1;
    for (const path of this.paths) {
      for (let t = 0; t <= 1; t += 0.02) {
        const point = path.getPointAt(t);
        if (position.distanceTo(new THREE.Vector3(point.x, 0, point.z)) < pathThreshold) {
          return false;
        }
      }
    }

    for (const tower of this.towers) {
      if (position.distanceTo(tower.group.position) < 1.6) {
        return false;
      }
    }

    return true;
  }

  private buildTower(type: TowerType, position: THREE.Vector3): void {
    const model = this.towerFactory.create(type);
    const tower = new Tower(type, position, model);
    this.towers.push(tower);
    this.scene.add(tower.group);
    this.spawnTowerSoldiers(position);
  }

  private spawnTowerSoldiers(position: THREE.Vector3): void {
    const offsets = [
      { x: 0.9, z: 0.4, angle: -Math.PI / 6 },
      { x: -0.9, z: 0.4, angle: Math.PI / 6 },
    ];

    for (const offset of offsets) {
      const soldierPos = new THREE.Vector3(position.x + offset.x, 0, position.z + offset.z);
      const model = this.soldierFactory.create();
      const soldier = new Soldier(soldierPos, offset.angle, model);
      this.soldiers.push(soldier);
      this.scene.add(soldier.group);
    }
  }

  private spawnInitialSoldiers(): void {
    const bunkerPos = this.world.objectivePosition;
    const positions = [
      { x: -1.6, z: 1.2, angle: Math.PI / 4 },
      { x: 1.6, z: 1.2, angle: -Math.PI / 4 },
      { x: -1.2, z: -1.4, angle: (3 * Math.PI) / 4 },
      { x: 1.2, z: -1.4, angle: (-3 * Math.PI) / 4 },
    ];

    for (const pos of positions) {
      const soldierPos = new THREE.Vector3(bunkerPos.x + pos.x, 0, bunkerPos.z + pos.z);
      const model = this.soldierFactory.create();
      const soldier = new Soldier(soldierPos, pos.angle, model);
      this.soldiers.push(soldier);
      this.scene.add(soldier.group);
    }
  }

  private spawnShell(): void {
    const target = this.pickShellTarget();
    const shell = new ArtilleryShell(
      target,
      { shell: this.materials.darkSteel, marker: this.materials.markerInvalid },
      this.shellRadius,
      this.shellDamage,
    );
    this.shells.push(shell);
    this.scene.add(shell.group);
  }

  private pickShellTarget(): THREE.Vector3 {
    const activeTowers = this.towers.filter((t) => t.active);
    if (activeTowers.length > 0) {
      const tower = activeTowers[Math.floor(Math.random() * activeTowers.length)];
      return tower.group.position.clone();
    }

    const min = this.world.validGround.min;
    const max = this.world.validGround.max;
    for (let i = 0; i < 10; i++) {
      const x = min.x + Math.random() * (max.x - min.x);
      const z = min.y + Math.random() * (max.y - min.y);
      const point = new THREE.Vector3(x, 0, z);
      if (point.distanceToSquared(this.objective.position) > 4) {
        return point;
      }
    }
    return new THREE.Vector3(0, 0, 0);
  }

  private findEnemyTargetTower(enemy: Enemy): Tower | null {
    if (enemy.stats.attackRange <= 0 || !enemy.active || enemy.reachedObjective) return null;

    let best: Tower | null = null;
    let bestDist = Infinity;
    for (const tower of this.towers) {
      if (!tower.active) continue;
      const dx = enemy.group.position.x - tower.group.position.x;
      const dz = enemy.group.position.z - tower.group.position.z;
      const distSq = dx * dx + dz * dz;
      if (distSq <= enemy.stats.attackRange * enemy.stats.attackRange && distSq < bestDist) {
        bestDist = distSq;
        best = tower;
      }
    }
    return best;
  }

  private cleanupDeadEntities(): void {
    for (let i = this.enemies.length - 1; i >= 0; i--) {
      const enemy = this.enemies[i];
      if (!enemy.active && enemy.reachedObjective) {
        this.scene.remove(enemy.group);
        enemy.dispose();
        this.enemies.splice(i, 1);
      } else if (!enemy.active && enemy.health <= 0) {
        this.scene.remove(enemy.group);
        enemy.dispose();
        this.enemies.splice(i, 1);
      }
    }

    for (let i = this.towers.length - 1; i >= 0; i--) {
      const tower = this.towers[i];
      if (!tower.active) {
        this.vfx.spawnExplosion(tower.group.position, 1.2, false);
        this.audio.explosion(0.9);
        this.scene.remove(tower.group);
        tower.dispose();
        this.towers.splice(i, 1);
      }
    }

    for (let i = this.soldiers.length - 1; i >= 0; i--) {
      const soldier = this.soldiers[i];
      if (!soldier.active) {
        this.vfx.spawnExplosion(soldier.group.position, 0.7, false);
        this.scene.remove(soldier.group);
        soldier.dispose();
        this.soldiers.splice(i, 1);
      }
    }

    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const projectile = this.projectiles[i];
      if (!projectile.active) {
        this.scene.remove(projectile.group);
        projectile.dispose();
        this.projectiles.splice(i, 1);
      }
    }

    for (let i = this.shells.length - 1; i >= 0; i--) {
      const shell = this.shells[i];
      if (!shell.active) {
        this.scene.remove(shell.group);
        shell.dispose();
        this.shells.splice(i, 1);
      }
    }
  }

  private updateHud(): void {
    const wave = Math.max(1, this.waveSystem.currentWaveIndex + 1);
    let status = '准备防御';
    if (this.gameState === 'playing') status = '战斗中';
    if (this.gameState === 'wave-complete') status = '波次完成';
    if (this.gameState === 'gameover') status = '防线失守';
    if (this.gameState === 'victory') status = '胜利';

    this.hud.update(this.lives, this.economy.money, wave, status, this.lives <= 5);
  }

  private publishDiagnostics(): void {
    const info = this.renderer.info;
    window.__THREE_GAME_DIAGNOSTICS__ = {
      frame: this.frame,
      elapsed: this.elapsed,
      lives: this.lives,
      money: this.economy.money,
      state: this.gameState,
      speed: this.speedMultiplier,
      wave: this.waveSystem.currentWaveIndex + 1,
      towers: this.towers.length,
      soldiers: this.soldiers.filter((s) => s.active).length,
      enemies: this.enemies.filter((e) => e.active).length,
      projectiles: this.projectiles.length,
      renderer: {
        calls: info.render.calls,
        triangles: info.render.triangles,
        geometries: info.memory.geometries,
        textures: info.memory.textures,
      },
      canvas: {
        clientWidth: this.canvas.clientWidth,
        clientHeight: this.canvas.clientHeight,
        width: this.canvas.width,
        height: this.canvas.height,
        dpr: Math.min(window.devicePixelRatio || 1, this.tuning.maxDpr),
      },
    };
  }
}
